import { Paper } from '@mantine/core';
import clsx from 'clsx';
import { useCallback, useMemo } from 'react';
import { Content } from 'src/contexts/ContentContext';
import { useMobileListingDrawerContext } from 'src/contexts/MobileListingDrawerContext/MobileListingDrawerContext';
import { useMultiSelectionContext } from 'src/contexts/MultiSelectionContext';
import { Checkbox } from 'src/core/interim/Checkbox';
import { vars } from 'src/core/themes';
import { Stack } from 'src/core/ui';
import { useUserHasFeature } from 'src/hooks/useUserHasFeature';
import { ChevronRightIcon } from 'src/svgs/Viagogo';
import { ListingGroupStatusSummary } from 'src/tables/ListingTable/configs/ListingGroupStatusSummary';
import { ContentId } from 'src/utils/constants/contentId';
import { getFlattenedListingIds } from 'src/utils/inventoryUtils';
import {
  Event,
  Feature,
  GroupType,
  Listing,
  ListingGroup,
} from 'src/WebApiController';

import * as styles from './ListingTilesV2.css';

export const ListingGroupTile = ({
  listingGroup,
  entities,
  event,
}: {
  listingGroup: ListingGroup;
  entities: Listing[];
  event: Event;
}) => {
  const hideHiddenByFilter = useUserHasFeature(Feature.HideHiddenByFilter);

  const { getSelection, setGroupItems, getGroupToggleState } =
    useMultiSelectionContext();
  const listingSelection = useMemo(
    () => getSelection(event.virtId),
    [event.virtId, getSelection]
  );

  const isGroupSelected = useMemo(
    () =>
      getGroupToggleState(event.virtId).isGroupSelected &&
      !listingSelection.items.itemIds.length,
    [event.virtId, getGroupToggleState, listingSelection.items.itemIds.length]
  );
  const isSelected = useMemo(() => {
    return (
      isGroupSelected ||
      listingSelection.items.itemIds.includes(String(listingGroup.id))
    );
  }, [isGroupSelected, listingSelection.items.itemIds, listingGroup.id]);

  const toggleListingSelection = useCallback(() => {
    const listingIdsToggled = [
      listingGroup,
      ...(listingGroup.groupItems ?? []),
      ...(listingGroup.groupItems ?? []).flatMap(
        (lg) => (lg as ListingGroup).groupItems ?? []
      ),
    ].map((l) => l.id);

    const selectedListingIds = isGroupSelected
      ? getFlattenedListingIds(entities).map((id) => String(id))
      : listingSelection.items.itemIds;

    // Current intended behavior: Either select all ids in listingGroup or none
    if (
      selectedListingIds.some((selected) =>
        listingIdsToggled.includes(parseInt(selected))
      )
    ) {
      // Deselect
      setGroupItems(
        event.virtId,
        selectedListingIds
          .map((id) => parseInt(id))
          .filter((id) => !listingIdsToggled.includes(id))
          .reduce(
            (r, l) => {
              r[l] = true;
              return r;
            },
            {} as Record<number, boolean>
          )
      );
    } else {
      // Add select
      selectedListingIds.push(...listingIdsToggled.map((lId) => String(lId)));
      setGroupItems(
        event.virtId,
        selectedListingIds
          .map((id) => parseInt(id))
          .reduce(
            (r, listingId) => {
              r[listingId] = true;
              return r;
            },
            {} as Record<number, boolean>
          )
      );
    }
  }, [
    entities,
    event.virtId,
    isGroupSelected,
    listingGroup,
    listingSelection.items.itemIds,
    setGroupItems,
  ]);

  const { onSelectListingGroup } = useMobileListingDrawerContext();

  const onViewClick = useCallback(async () => {
    if (listingSelection.isSingleGroupMultiSelect) {
      toggleListingSelection();
      return;
    }

    onSelectListingGroup(listingGroup);
  }, [
    listingSelection.isSingleGroupMultiSelect,
    onSelectListingGroup,
    listingGroup,
    toggleListingSelection,
  ]);

  if (
    hideHiddenByFilter &&
    listingGroup.groupType !== GroupType.Active &&
    listingGroup.groupType !== GroupType.Leader
  ) {
    return null;
  }

  return (
    <Paper
      withBorder
      shadow="sm"
      p={vars.spacing.m}
      radius={vars.borderRadius.m}
      bg={vars.color.backgroundPrimary}
      onClick={onViewClick}
      className={clsx({ [styles.tileSelected]: isSelected })}
    >
      <Stack
        direction="row"
        alignItems="center"
        justifyContent="spaceBetween"
        style={{ color: vars.color.textPrimary }}
      >
        <Stack direction="row" alignItems="center" gap="m">
          {listingSelection.isSingleGroupMultiSelect && (
            <div
              onClick={(e) => {
                /* Prevent onViewClick from being called twice and interfering with selection */
                e.preventDefault();
                e.stopPropagation();
              }}
            >
              <Checkbox
                checked={isSelected}
                checkboxSizeVariant="light"
                onChange={onViewClick}
              />
            </div>
          )}
          <ListingGroupStatusSummary listingGroup={listingGroup} />

          <span>
            {listingGroup.groupType === GroupType.Active ? (
              <Content id={ContentId.Active} />
            ) : listingGroup.groupType === GroupType.Inactive ? (
              <Content id={ContentId.Queued} />
            ) : listingGroup.groupType === GroupType.FullySold ? (
              <Content id={ContentId.Sold} />
            ) : listingGroup.groupType === GroupType.Invisible ? (
              <Content id={ContentId.HiddenDueToFilter} />
            ) : (
              listingGroup.groupName
            )}
          </span>
        </Stack>
        <Stack className={styles.secondaryInfo} gap="m" alignItems="center">
          <span className={styles.priceLabel}>
            {(listingGroup.unitCst?.disp ?? listingGroup.faceValue?.disp) &&
              `(${listingGroup.unitCst?.disp ?? listingGroup.faceValue?.disp})`}
          </span>
          <ChevronRightIcon
            fill={vars.color.textSecondary}
            size={vars.iconSize.s}
          />
        </Stack>
      </Stack>
    </Paper>
  );
};
